// Le bloc « Invitations » de la fiche d'une étude.
//
// On invite un participant à un créneau ; il accepte ou décline depuis un lien
// personnel, sans compte ; à l'acceptation, le lien Google Meet ou Teams est
// créé dans le compte de la personne qui a invité.
//
// Deux choses ne se voient qu'UNE fois, parce que le serveur n'en garde que
// l'empreinte : le lien d'invitation, à la création et à son renouvellement.
import { useState, type FormEvent } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, buttonClass, Chip, chipState, ErrorBanner, Field, Note, Select, Spinner, CopyButton } from '../components/ui';
import { hrefFor, Link } from '../router';
import { FormShell } from '../write/Forms';
import { Drawer } from '../write/Drawer';
import {
  PROVIDER_LABELS,
  useCreateInvitation,
  useCreateInvitationBatch,
  useDeleteInvitation,
  useGenerateMeeting,
  useReproposeInvitation,
  useRotateInvitation,
  useSetMeetingUrl,
  useStudyInvitations,
  useVisioAccounts,
  BOOKING_LABELS,
  useSetOutcome,
  type BatchResult,
  type BookingStatus,
  type Outcome,
  type SessionInvitation,
  type VisioProvider,
} from './api';
import { usePeople } from '../people/api';
import { googleCalendarLink } from '../calendar/api';
import '../calendar/calendar.css';
import { FormatPicker, SlotPicker, type Format } from './SlotPicker';
import { InvitationMessage } from './InvitationMessage';
import { PeoplePicker } from '../people/PeoplePicker';
import './invitations.css';

const errorOf = (cause: unknown) =>
  cause instanceof ApiRequestError ? cause.message : 'Une erreur inattendue est survenue.';

const linkFor = (token: string) => `${window.location.origin}${hrefFor(`/rdv/${token}`)}`;

function formatSlot(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Valeur d'un champ `datetime-local`, à l'heure du poste. */
function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function tomorrowMorning(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toLocalInput(d);
}

const DURATIONS = [15, 30, 45, 60, 90, 120];

// ---------------------------------------------------------------------------
// LE LIEN, montré une fois
// ---------------------------------------------------------------------------

function FreshLink({ name, link, mailSent, onDone }: { name: string; link: string; mailSent: boolean; onDone: () => void }) {
  return (
    <div className="inv-fresh" role="status">
      <p className="inv-note">
        {mailSent ? (
          <>L’invitation est partie par e-mail à <strong>{name}</strong>. Voici aussi son lien personnel.</>
        ) : (
          <>Lien personnel de <strong>{name}</strong> : envoyez-le-lui vous-même.</>
        )}{' '}
        <strong>Il ne sera plus affiché ensuite.</strong>
      </p>
      <input className="zy-input inv-link" readOnly value={link} aria-label="Lien d’invitation" onFocus={(e) => e.target.select()} />
      <div className="inv-actions">
        <CopyButton text={link} label="Copier le lien" />
        <Button type="button" variant="ghost" onClick={onDone}>Fermer</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// INVITER
// ---------------------------------------------------------------------------

/** Ce que deviendra le lien de la réunion, selon l'outil choisi et MON compte. */
function ProviderNote({ provider }: { provider: VisioProvider }) {
  const accounts = useVisioAccounts();
  const account = accounts.data?.providers.find((p) => p.provider === provider);
  if (!account) return null;
  const tool = PROVIDER_LABELS[provider];
  if (account.connected) {
    return (
      <Note>
        Dès que la personne accepte, le lien {tool} est créé dans votre compte
        {account.accountEmail ? ` ${account.accountEmail}` : ''}.
      </Note>
    );
  }
  return (
    <Note>
      {account.configured
        ? `Votre compte ${tool} n’est pas connecté : `
        : `${tool} n’est pas encore configuré sur ce serveur : `}
      à l’acceptation, vous collerez le lien de la réunion à la main.
      {account.configured ? (
        <>
          {' '}
          <Link to="/compte" className="card-title-link">Connecter mon compte</Link>
        </>
      ) : null}
    </Note>
  );
}

/** Ce qui s'affiche après un envoi à plusieurs : qui a reçu l'e-mail, et le
 *  lien (montré une fois) de ceux à qui il faut l'envoyer soi-même. */
function BatchDone({ result, onDone }: { result: BatchResult; onDone: () => void }) {
  const mailed = result.sent.filter((s) => s.mailSent);
  const byHand = result.sent.filter((s) => !s.mailSent);
  return (
    <div className="inv-fresh" role="status">
      <p className="inv-note">
        <strong>{result.sent.length} invitation{result.sent.length > 1 ? 's' : ''} créée{result.sent.length > 1 ? 's' : ''}</strong>
        {mailed.length ? ` · ${mailed.length} envoyée${mailed.length > 1 ? 's' : ''} par e-mail` : ''}.
      </p>
      {byHand.length ? (
        <>
          <p className="inv-note">
            Envoyez vous-même leur lien personnel à ces personnes. <strong>Il ne sera plus affiché ensuite.</strong>
          </p>
          <ul className="inv-links">
            {byHand.map((s) => (
              <li key={s.personId}>
                <strong>{s.name}</strong>
                <input className="zy-input inv-link" readOnly value={linkFor(s.token)} aria-label={`Lien de ${s.name}`} onFocus={(e) => e.target.select()} />
                <CopyButton text={linkFor(s.token)} label="Copier le lien" />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {result.skipped.length ? (
        <ul className="inv-links">
          {result.skipped.map((s) => <li key={s.personId} className="inv-sub">Non invitée : {s.reason}</li>)}
        </ul>
      ) : null}
      <div className="inv-actions">
        <Button type="button" variant="ghost" onClick={onDone}>Fermer</Button>
      </div>
    </div>
  );
}

/**
 * Inviter. Depuis une étude : une ou plusieurs personnes. Depuis la fiche d'un
 * entretien (`interviewId`) : UNE personne, et l'entretien porte ce rendez-vous.
 */
export function InviteForm({ projectId, studyId, defaultSubject, onDone, interviewId, defaultName = '', studyLabel = '' }: {
  projectId: string; studyId: string; defaultSubject: string; onDone: () => void;
  interviewId?: string; defaultName?: string;
  /** L'objectif de l'étude, repris dans le message proposé. */
  studyLabel?: string;
}) {
  const create = useCreateInvitation(studyId);
  const batch = useCreateInvitationBatch(studyId);
  const people = usePeople(projectId);
  const hasDirectory = (people.data?.people.length ?? 0) > 0;
  // Par défaut on pioche dans l'annuaire ; « à la main » reste possible.
  const [who, setWho] = useState<'annuaire' | 'manuel' | null>(null);
  const source = who ?? (hasDirectory ? 'annuaire' : 'manuel');
  const [personIds, setPersonIds] = useState<string[]>([]);
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [when, setWhen] = useState<'fixe' | 'choix'>('choix');
  const [slot, setSlot] = useState(tomorrowMorning);
  const [slots, setSlots] = useState<string[]>([]);
  const [duration, setDuration] = useState(45);
  // Formats proposés (055) : plusieurs cochés, la personne choisit le sien.
  const [formats, setFormats] = useState<Format[]>(['google_meet']);
  const [location, setLocation] = useState('');
  const [message, setMessage] = useState('');
  const [fresh, setFresh] = useState<{ name: string; link: string; mailSent: boolean } | null>(null);
  const [batchDone, setBatchDone] = useState<BatchResult | null>(null);

  const start = new Date(slot);
  const chosenSlots = slots.map((s) => new Date(s)).filter((d) => !Number.isNaN(d.getTime()));
  const onsite = formats.includes('onsite');
  const whenReady = when === 'fixe' ? !Number.isNaN(start.getTime()) : chosenSlots.length > 0;
  const whoReady = source === 'annuaire' ? personIds.length > 0 : name.trim().length > 0;
  const ready = whoReady && subject.trim().length > 0 && whenReady && formats.length > 0
    && (!onsite || location.trim().length > 0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    // Les champs sont à l'heure du poste : on envoie l'instant, et le fuseau
    // pour que les e-mails écrivent la même heure.
    const common = {
      subject,
      message: message.trim() || null,
      ...(when === 'fixe'
        ? { startsAt: start.toISOString() }
        : { slots: chosenSlots.map((d) => d.toISOString()) }),
      durationMin: duration,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      formats,
      location: onsite ? location.trim() : null,
    };
    try {
      if (source === 'annuaire' && !interviewId) {
        setBatchDone(await batch.mutateAsync({ ...common, personIds }));
      } else {
        const res = await create.mutateAsync({
          ...common,
          ...(source === 'annuaire'
            ? { personId: personIds[0], inviteeName: '', inviteeEmail: null }
            : { inviteeName: name, inviteeEmail: email.trim() || null }),
          ...(interviewId ? { interviewId } : {}),
        });
        setFresh({ name: res.invitation.invitee_name, link: linkFor(res.token), mailSent: res.mailSent });
      }
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  if (fresh) return <FreshLink {...fresh} onDone={onDone} />;
  if (batchDone) return <BatchDone result={batchDone} onDone={onDone} />;

  const submitLabel = source === 'annuaire'
    ? (personIds.length > 1 ? `Inviter ${personIds.length} personnes` : 'Inviter la personne choisie')
    : 'Créer l’invitation';

  return (
    <FormShell
      onSubmit={submit}
      submitting={create.isPending || batch.isPending}
      error={create.error ?? batch.error}
      submitLabel={submitLabel}
      disabled={!ready}
    >
      <fieldset className="inv-choice">
        <legend className="zy-field__label">Qui invitez-vous ?</legend>
        <label className="ppl-check">
          <input className="zy-radio" type="radio" name="inv-who" checked={source === 'annuaire'} onChange={() => setWho('annuaire')} />
          Des personnes de l’annuaire
        </label>
        <label className="ppl-check">
          <input className="zy-radio" type="radio" name="inv-who" checked={source === 'manuel'} onChange={() => setWho('manuel')} />
          Une personne saisie à la main
        </label>
      </fieldset>
      {source === 'annuaire' ? (
        <PeoplePicker projectId={projectId} selected={personIds} onChange={setPersonIds} max={interviewId ? 1 : 50} />
      ) : (
        <div className="field-row">
          <Field label="Participant *" value={name} onChange={(e) => setName(e.target.value)} placeholder="Karim B." autoComplete="off" />
          <Field
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="karim@exemple.fr"
            autoComplete="off"
            hint="Facultatif : sans adresse, vous envoyez le lien vous-même."
          />
        </div>
      )}
      <Field
        label="Sujet *"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        hint="Ce que la personne invitée lira. N’y mettez pas votre hypothèse : elle orienterait ses réponses."
      />
      <fieldset className="inv-choice">
        <legend className="zy-field__label">Créneau</legend>
        <label className="ppl-check">
          <input className="zy-radio" type="radio" name="inv-when" checked={when === 'choix'} onChange={() => setWhen('choix')} />
          Plusieurs créneaux : la personne choisit
        </label>
        <label className="ppl-check">
          <input className="zy-radio" type="radio" name="inv-when" checked={when === 'fixe'} onChange={() => setWhen('fixe')} />
          Un créneau fixé
        </label>
      </fieldset>
      {when === 'choix' ? (
        <SlotPicker projectId={projectId} durationMin={duration} value={slots} onChange={setSlots} />
      ) : null}
      <div className="field-row">
        {when === 'fixe' ? (
          <Field label="Date et heure *" type="datetime-local" voice={false} value={slot} onChange={(e) => setSlot(e.target.value)} />
        ) : null}
        <label className="zy-field">
          <span className="zy-field__label">Durée</span>
          <Select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
            {DURATIONS.map((d) => (
              <option key={d} value={d}>{d} minutes</option>
            ))}
          </Select>
        </label>
      </div>
      <div className="zy-field">
        <span className="zy-field__label">Formats proposés</span>
        <FormatPicker value={formats} onChange={setFormats} location={location} onLocation={setLocation} />
        {formats.filter((f) => f !== 'onsite').map((f) => <ProviderNote key={f} provider={f as VisioProvider} />)}
      </div>
      <InvitationMessage
        projectId={projectId}
        studyLabel={studyLabel}
        durationMin={duration}
        formats={formats}
        location={location}
        value={message}
        onChange={setMessage}
      />
    </FormShell>
  );
}

// ---------------------------------------------------------------------------
// LA LISTE
// ---------------------------------------------------------------------------

/** Le statut de RÉSERVATION (055) : invité, réservé, décliné, réalisé, absent, annulé. */
function BookingStatusChip({ invitation }: { invitation: SessionInvitation }) {
  const booking = invitation.booking ?? 'invite';
  if (booking === 'invite') {
    // Au choix : « sans réponse » quand le dernier créneau proposé est passé.
    const last = invitation.starts_at ?? (invitation.slot_options ?? []).at(-1) ?? null;
    if (last !== null && new Date(last).getTime() < Date.now()) return <Chip state="vide">Sans réponse</Chip>;
  }
  const b = BOOKING_LABELS[booking];
  return <Chip state={chipState(b.tone)}>{b.label}</Chip>;
}

/** Après la séance : réalisé, absent, annulé. Revenir à « — » rend le statut de la réponse. */
function OutcomeSelect({ invitation, studyId }: { invitation: SessionInvitation; studyId: string }) {
  const set = useSetOutcome(studyId);
  const booked = invitation.status === 'accepted';
  return (
    <Select
      className="booking-select"
      aria-label={`Suivi de ${invitation.invitee_name}`}
      value={invitation.outcome ?? ''}
      disabled={set.isPending}
      onChange={(e) => set.mutate({ id: invitation.id, outcome: (e.target.value || null) as Outcome | null })}
    >
      <option value="">Suivi…</option>
      {booked ? <option value="done">Réalisé</option> : null}
      {booked ? <option value="no_show">Absent</option> : null}
      <option value="cancelled">Annulé</option>
    </Select>
  );
}

/** Les compteurs de l'étude : combien d'invités, de réservés… */
function BookingCounts({ invitations }: { invitations: SessionInvitation[] }) {
  const counts = new Map<BookingStatus, number>();
  for (const i of invitations) counts.set(i.booking ?? 'invite', (counts.get(i.booking ?? 'invite') ?? 0) + 1);
  return (
    <div className="booking-counts" aria-label="Suivi des réservations">
      {(Object.keys(BOOKING_LABELS) as BookingStatus[]).filter((b) => counts.get(b)).map((b) => (
        <Chip key={b} state={chipState(BOOKING_LABELS[b].tone)}>
          {BOOKING_LABELS[b].label} · {counts.get(b)}
        </Chip>
      ))}
    </div>
  );
}

/** La colonne « Visioconférence » : le lien, ou ce qu'il manque pour l'avoir. */
function MeetingCell({ invitation, studyId, canWrite }: { invitation: SessionInvitation; studyId: string; canWrite: boolean }) {
  const generate = useGenerateMeeting(studyId);
  const paste = useSetMeetingUrl(studyId);
  const [pasting, setPasting] = useState(false);
  const [url, setUrl] = useState('');
  // Formats au choix (055), tant que la personne n'a pas répondu.
  if (invitation.status === 'pending' && (invitation.format_options?.length ?? 0) > 1) {
    const names = { google_meet: 'Meet', teams: 'Teams', onsite: 'sur place' } as const;
    return <>Au choix : {invitation.format_options!.map((f) => names[f]).join(', ')}</>;
  }
  // Sur place : pas de lien, le lieu tient lieu de rendez-vous.
  if (invitation.mode === 'onsite') {
    return (
      <>
        Sur place
        <span className="inv-sub">{invitation.location}</span>
      </>
    );
  }
  const tool = invitation.provider ? PROVIDER_LABELS[invitation.provider] : 'la visioconférence';

  if (invitation.meeting_url) {
    return (
      <>
        <a className="card-title-link" href={invitation.meeting_url} target="_blank" rel="noreferrer noopener">
          Ouvrir {tool}
        </a>
        <span className="inv-sub">
          <CopyLink text={invitation.meeting_url} />
        </span>
      </>
    );
  }
  if (invitation.status !== 'accepted') {
    return (
      <>
        {tool}
        {invitation.status === 'pending' ? <span className="inv-sub">lien créé à l’acceptation</span> : null}
      </>
    );
  }

  const failure = generate.error ?? paste.error;
  return (
    <>
      <Chip state="avertissement">Lien à ajouter</Chip>
      {invitation.meeting_error && !failure ? <span className="inv-sub">{invitation.meeting_error}</span> : null}
      {failure ? <span className="inv-sub inv-sub-error">{errorOf(failure)}</span> : null}
      {canWrite ? (
        pasting ? (
          <form
            className="inv-paste"
            onSubmit={(e) => {
              e.preventDefault();
              generate.reset();
              paste.mutate({ id: invitation.id, url }, { onSuccess: () => setPasting(false) });
            }}
          >
            <input
              className="zy-input"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://meet.google.com/…"
              aria-label="Lien de la réunion"
              required
            />
            <Button type="submit" variant="accent" loading={paste.isPending}>Enregistrer</Button>
            <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => setPasting(false)}>Annuler</Button>
          </form>
        ) : (
          <span className="inv-row-actions">
            <Button variant="link" size="s" className="u-push-end"
              type="button"
              disabled={generate.isPending}
              onClick={() => { paste.reset(); generate.mutate(invitation.id); }}
            >
              {generate.isPending ? 'Création…' : `Créer le lien ${tool}`}
            </Button>
            <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => setPasting(true)}>Coller un lien</Button>
          </span>
        )
      ) : null}
    </>
  );
}

function CopyLink({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button variant="link" size="s" className="u-push-end"
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => setCopied(true), () => setCopied(false));
      }}
    >
      {copied ? 'Lien copié' : 'Copier le lien'}
    </Button>
  );
}

/** Proposer une autre date : de nouveaux créneaux (dans l'agenda, ou cochés), un mot. */
function ReproposeForm({ invitation, studyId, onDone }: { invitation: SessionInvitation; studyId: string; onDone: (link: string | null, mailSent: boolean) => void }) {
  const repropose = useReproposeInvitation(studyId);
  const [slots, setSlots] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const booked = invitation.status === 'accepted' && invitation.starts_at;
  return (
    <form
      className="write-form"
      onSubmit={(e) => {
        e.preventDefault();
        repropose.mutate(
          { id: invitation.id, slots, message: message.trim() || undefined },
          { onSuccess: (r) => onDone(linkFor(r.token), r.mailSent) },
        );
      }}
    >
      <p className="zy-texte-muet">
        {booked
          ? <>Le rendez-vous du <strong>{formatSlot(invitation.starts_at!)}</strong> sera retiré de l’agenda de {invitation.invitee_name}, qui recevra un nouveau lien pour choisir une autre date.</>
          : <>{invitation.invitee_name} recevra un nouveau lien avec ces créneaux ; l’ancien lien cesse de fonctionner.</>}
      </p>
      <div className="zy-field">
        <span className="zy-field__label">Nouveaux créneaux</span>
        <SlotPicker projectId={invitation.project_id} durationMin={invitation.duration_min} value={slots} onChange={setSlots} />
      </div>
      <label className="zy-field">
        <span className="zy-field__label">Un mot (facultatif)</span>
        <textarea
          className="zy-input"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Désolé, un imprévu m’oblige à décaler notre échange. Choisissez le créneau qui vous convient."
        />
      </label>
      {repropose.error ? <ErrorBanner>{errorOf(repropose.error)}</ErrorBanner> : null}
      <div className="form-actions">
        <Button type="submit" variant="accent" loading={repropose.isPending} disabled={slots.length === 0}>
          Envoyer la nouvelle proposition
        </Button>
      </div>
    </form>
  );
}

function BookingRow({ invitation, studyId, canWrite }: { invitation: SessionInvitation; studyId: string; canWrite: boolean }) {
  const rotate = useRotateInvitation();
  const remove = useDeleteInvitation(studyId);
  const cancel = useSetOutcome(studyId);
  const [confirming, setConfirming] = useState<null | 'delete' | 'cancel'>(null);
  const [reproposing, setReproposing] = useState(false);
  const [fresh, setFresh] = useState<{ link: string; mailSent: boolean } | null>(null);
  const failure = rotate.error ?? remove.error ?? cancel.error;
  // Réservé et à venir : annuler ou supprimer prévient la personne.
  const upcoming = invitation.status === 'accepted' && invitation.outcome !== 'cancelled'
    && invitation.starts_at !== null && new Date(invitation.starts_at).getTime() > Date.now();

  return (
    <>
      <tr>
        <td>
          <strong>{invitation.invitee_name}</strong>
          {invitation.invitee_email ? <span className="inv-sub">{invitation.invitee_email}</span> : null}
        </td>
        <td>
          {invitation.starts_at
            ? (
              <>
                {formatSlot(invitation.starts_at)}
                {invitation.status === 'accepted' ? (
                  <a
                    className="inv-sub card-title-link"
                    href={googleCalendarLink({
                      title: `${invitation.subject} — ${invitation.invitee_name}`,
                      startsAt: invitation.starts_at,
                      durationMin: invitation.duration_min,
                      details: invitation.meeting_url ?? invitation.location ?? '',
                    })}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Ajouter à Google Agenda
                  </a>
                ) : null}
              </>
            )
            : invitation.status === 'pending'
              ? `À choisir parmi ${invitation.slot_options?.length ?? 0} créneaux`
              : 'Aucun créneau choisi'}
          <span className="inv-sub">{invitation.duration_min} min · {invitation.subject}</span>
        </td>
        <td>
          <BookingStatusChip invitation={invitation} />
          {invitation.rescheduled_at && invitation.booking === 'modifie' ? (
            <span className="inv-sub">modifié le {new Date(invitation.rescheduled_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          ) : null}
          {invitation.decline_reason ? <span className="inv-sub">« {invitation.decline_reason} »</span> : null}
        </td>
        <td>
          <MeetingCell invitation={invitation} studyId={studyId} canWrite={canWrite} />
        </td>
        {canWrite ? (
          <td className="row-actions">
            {confirming ? (
              <>
                <span className="inv-sub">
                  {confirming === 'cancel'
                    ? 'Annuler ce rendez-vous ? La personne est prévenue, il quitte son agenda.'
                    : upcoming
                      ? 'Supprimer ? Le rendez-vous est d’abord annulé auprès de la personne.'
                      : 'Supprimer cette invitation ?'}
                </span>
                <Button variant="link-danger" size="s"
                  type="button"
                  disabled={remove.isPending || cancel.isPending}
                  onClick={() => (confirming === 'cancel'
                    ? cancel.mutate({ id: invitation.id, outcome: 'cancelled' }, { onSuccess: () => setConfirming(null) })
                    : remove.mutate(invitation.id))}
                >
                  Confirmer
                </Button>
                <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => setConfirming(null)}>Garder</Button>
              </>
            ) : (
              <>
                <OutcomeSelect invitation={invitation} studyId={studyId} />
                {/* Tout reste modifiable : une autre date, à tout moment. */}
                <Button variant="link" size="s" className="u-push-end"
                  type="button"
                  title="Envoie de nouveaux créneaux à la personne, avec un nouveau lien."
                  onClick={() => setReproposing(true)}
                >
                  Autre date
                </Button>
                {upcoming ? (
                  <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => setConfirming('cancel')}>
                    Annuler le RDV
                  </Button>
                ) : null}
                {invitation.status === 'pending' ? (
                  <Button variant="link" size="s" className="u-push-end"
                    type="button"
                    disabled={rotate.isPending}
                    title="Crée un nouveau lien personnel ; l’ancien cesse de fonctionner."
                    onClick={() => rotate.mutate(invitation.id, { onSuccess: (r) => setFresh({ link: linkFor(r.token), mailSent: false }) })}
                  >
                    Nouveau lien
                  </Button>
                ) : null}
                <Button variant="link-danger" size="s"
                  type="button"
                  title="Supprime l’invitation, avec le nom et l’adresse du participant."
                  onClick={() => setConfirming('delete')}
                >
                  Supprimer
                </Button>
                <Drawer
                  open={reproposing}
                  title={`Autre date pour ${invitation.invitee_name}`}
                  description="Nouveaux créneaux, nouveau lien : la personne choisit de nouveau."
                  wide
                  onClose={() => setReproposing(false)}
                >
                  <ReproposeForm
                    invitation={invitation}
                    studyId={studyId}
                    onDone={(link, mailSent) => { setReproposing(false); if (link) setFresh({ link, mailSent }); }}
                  />
                </Drawer>
              </>
            )}
          </td>
        ) : null}
      </tr>
      {fresh || failure ? (
        <tr>
          <td colSpan={canWrite ? 5 : 4}>
            {fresh ? (
              <FreshLink name={invitation.invitee_name} link={fresh.link} mailSent={fresh.mailSent} onDone={() => setFresh(null)} />
            ) : (
              <ErrorBanner>{errorOf(failure)}</ErrorBanner>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function StudyInvitationsSection({
  projectId,
  studyId,
  canWrite,
}: {
  projectId: string;
  studyId: string;
  canWrite: boolean;
  /** Sujet proposé, selon le type d'étude. */
  defaultSubject: string;
}) {
  const query = useStudyInvitations(studyId);
  return (
    <section className="test-block">
      <div className="block-head">
        <h2 className="t-h3">Invitations</h2>
        {canWrite ? (
          // Inviter en masse a sa page (055) : participants, créneaux et formats à cocher.
          <Link
            to={`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(studyId)}/inviter`}
            className={buttonClass('accent')}
          >
            Inviter des participants
          </Link>
        ) : null}
      </div>
      {query.isPending ? (
        <Spinner label="Chargement des invitations" />
      ) : query.isError ? (
        <ErrorBanner>{errorOf(query.error)}</ErrorBanner>
      ) : query.data.invitations.length === 0 ? (
        <p className="card-note">
          Personne n’est encore invité. Une invitation propose un créneau, à distance (Google Meet ou Microsoft
          Teams : le lien de la réunion est créé quand la personne accepte) ou sur place (avec le lieu du rendez-vous).
        </p>
      ) : (
        <>
          <BookingCounts invitations={query.data.invitations} />
          {query.data.demo ? (
            <p className="inv-note">
              Démonstration : les liens Meet et Teams ont la forme des vrais, mais n’ouvrent aucune réunion.
            </p>
          ) : null}
          <div className="zy-table-cadre">
            <table className="zy-table inv-table">
              <thead>
                <tr>
                  <th>Participant</th>
                  <th>Créneau</th>
                  <th>Réservation</th>
                  <th>Lieu ou lien</th>
                  {canWrite ? <th aria-label="Actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {query.data.invitations.map((i) => (
                  <BookingRow key={i.id} invitation={i} studyId={studyId} canWrite={canWrite} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
